<template>
  <div class="collapsible">
    <div
      class="collapsible__header"
      @click="open = !open"
    >
      <div class="icon">
        <svg
          height="24"
          width="24"
        >
          <use xlink:href="/icons/sprite.svg#i-swap2" />
        </svg>
      </div>
      <div class="info">
        <p class="info__title">
          {{ t("selected_items_count", { n: selectedItems.length }) }}
        </p>
        <ui-price :price="tradeUpStore.calculates?.cost" />
      </div>
      <button
        v-if="selectedItems.length"
        :class="{ opened: open }"
        type="button"
      >
        <i class="i-arrow" />
      </button>
    </div>
    <transition
      v-if="selectedItems.length"
      name="collapse"
    >
      <div
        v-if="open"
        class="collapsible__body"
      >
        <transition-group
          :key="tradeUpStore.currentFullObject.updated_at"
          name="fade-items"
        >
          <PagesTradeUpComponentsContractSideComponentsItemRow
            v-for="(item, index) in selectedItems"
            :key="`${item.market_hash_name}-selected`"
            v-model:item="selectedItems[index]"
            @remove-item="selectedItems.splice(index, 1)"
          />
        </transition-group>
      </div>
    </transition>

    <div class="collapsible__footer">
      <div class="footer-item">
        <p class="footer-item__label">
          {{ t("average_float") }}
        </p>
        <p class="footer-item__value">
          {{ tradeUpStore.calculates?.avgFloat }}
        </p>
      </div>
      <div class="line" />
      <div class="footer-item">
        <div class="footer-item__label">
          <span>
            {{ t("estimated_profit") }}
          </span>
          <div class="info-tooltip">
            <svg
              class="info-svg"
              height="20"
              width="20"
            >
              <use xlink:href="/icons/sprite.svg#i-info" />
            </svg>
            <div class="collapse__tooltip">
              <div class="tooltip__title">
                <div class="tooltip__title-left">
                  <ui-price
                    :price="calculates?.mostPossiblePrice"
                  />
                  -
                  <ui-price :price="calculates?.cost" />
                </div>
                =
                <div
                  :class="{
                    red: calculates?.profit <= 0,
                    green: calculates?.profit > 0,
                  }"
                  class="tooltip__title-right"
                >
                  <ui-price
                    :price="calculates?.profit || currentFullObject.profit"
                  />
                  (<span>
                    {{ calculates?.profitPercentage }}% </span>)
                </div>
              </div>
              <p class="tooltip__text">
                {{ t("trade_up.formula") }}
              </p>
            </div>
          </div>
        </div>
        <p
          :class="{
            red: calculates?.profit <= 0,
            green: calculates?.profit > 0,
          }"
          class="footer-item__value"
        >
          <ui-price :price="calculates?.profit" />
          ({{ calculates?.profitPercentage }}%)
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useResize } from '~/composables/resize.js';
import { useTradeUpStore } from '~/store/trade-up';

const currW = useResize();
const tradeUpStore = useTradeUpStore();

const selectedItems = computed(() => tradeUpStore.selectedItems);
const calculates = computed(() => tradeUpStore.calculates);
const currentFullObject = computed(() => tradeUpStore.currentFullObject);

const { t } = useI18n();
const open = ref(currW.value > 989);
onMounted(() => {
  if (currW.value > 989)
    open.value = true;
});
</script>

<style lang="scss" scoped>
@use "sass:color";

.collapsible {
  width: 100%;
  gap: 16px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: var(--surfaces-variable-container);
  will-change: max-height;

  &__header {
    cursor: pointer;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    width: 100%;
    padding: 12px 12px 0 12px;

    .icon {
      background: var(--accent-secondary);
      width: 40px;
      height: 40px;
      border-radius: 8px;
      padding: 8px;
      color: #fff;
    }

    .info {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-right: auto;

      &:deep(.price) {
        overflow: hidden;
        color: var(--surfaces-on-surface-variable);
        text-overflow: ellipsis;
        font-size: 14px;
        font-weight: 400;
        line-height: 18px; /* 128.571% */
      }

      .info__title {
        overflow: hidden;
        color: var(--surfaces-on-surface);
        text-overflow: ellipsis;
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 18px; /* 128.571% */
      }
    }

    .opened {
      transform: rotate(180deg);
    }
  }

  &__body {
    padding: 0 0 0 12px;
    margin-right: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 350px; /* подбери подходящее значение */
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 8px;
    }

    /* Трек */
    &::-webkit-scrollbar-track {
      border-radius: 10px;
      padding: 4px;
      background: transparent;
    }

    /* Ползунок */
    &::-webkit-scrollbar-thumb {
      background-color: var(--surfaces-outline);
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;
      border-right: 0; // Убираем левую границу
      border-left: 4px solid transparent; // Оставляем правую границу для создания эффекта узкого ползунка
      border-radius: 4px;
      background-clip: padding-box; // Отсекает фон по области паддинга, сохраняя эффект отступа
    }

    /* Ползунок при наведении */
    &::-webkit-scrollbar-thumb:hover {
      background-color: color-mix(in srgb, var(--surfaces-outline) 95%, white);
    }
  }

  &__footer {
    border-radius: 0 0 8px 8px;
    border-top: 1px solid var(--surfaces-outline);
    background: var(--surfaces-highest-container);
    display: flex;
    align-items: center;
    padding: 8px 20px;
    gap: 20px;

    .line {
      width: 1px;
      height: 18px;
      background: #636e84;
    }

    .footer-item {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px;

      &__label {
        font-size: 14px;
        font-weight: 400;
        line-height: 18px; /* 128.571% */
        display: flex;
        gap: 4px;
        align-items: center;
        color: var(--surfaces-on-surface-variable);
        margin-right: 8px;
      }

      &__value {
        //color: var(--surfaces-on-surface);
        font-size: 14px;
        font-weight: 600;
        line-height: 18px; /* 128.571% */
      }

      .info-tooltip {
        position: relative;

        &:hover .collapse__tooltip {
          display: flex;
        }

        .info-svg {
          position: relative;
          top: 2px;
        }

        .collapse__tooltip {
          display: none;
          position: absolute;
          bottom: 110%;
          left: 12px;
          transform: translateX(-50%);
          border-radius: 8px;
          border: 1px solid var(--surfaces-outline);
          background: var(--surfaces-container);
          padding: 12px;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
          align-self: stretch;

          &::after {
            content: "";
            position: absolute;
            top: 100%;
            left: 50%;
            margin-left: -5px;
            border-width: 5px;
            border-style: solid;
            border-color: var(--surfaces-outline) transparent transparent transparent;
          }
        }

        .tooltip {
          min-width: max-content;

          &__title,
          &__title-left,
          &__title-right {
            display: flex;
            align-items: center;
            gap: 2px;
            color: var(--surfaces-on-surface);
            font-size: 14px;
            font-weight: 600;
            line-height: 18px; /* 128.571% */
            &.red {
              color: var(--destructive);
            }

            &.green {
              color: var(--accent-primary);
            }
          }
        }
      }
    }

    @media screen and (max-width: 768px) {
      display: block;
      .line {
        height: 5px;
        width: 0;
      }
    }
  }
}

/* Анимация сворачивания/разворачивания */
.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.collapse-enter-active,
.collapse-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
}

.collapse-enter-to,
.collapse-leave-from {
  max-height: 350px; /* подбери подходящее значение */
  opacity: 1;
  overflow: hidden;
}

.fade-items-enter-active,
.fade-items-leave-active {
  transition: all 0.3s ease;
}

.fade-items-enter-from,
.fade-items-leave-to {
  opacity: 0;
  transform: translateX(130px);
}
</style>
